import type { BotDifficulty } from "../../../bot/difficulty";
import type { Player } from "../../../game";
import type { MatchRecordResult, MatchRecordV1 } from "./matchRecord";
import type { StorageFailure } from "./storagePort";

export type MatchHistorySummary = Readonly<{
  id: string;
  completedAt: number;
  mode: "bot" | "local";
  result: MatchRecordResult;
  moveCount: number;
  humanPlayer?: Player;
  difficulty?: BotDifficulty;
}>;

export type HistoryLoadResult<T> =
  | Readonly<{ kind: "loaded"; value: T }>
  | Readonly<{ kind: "unavailable"; reason: StorageFailure }>;

export type HistoryMutationResult =
  Readonly<{ kind: "committed" }> | Readonly<{ kind: "unavailable"; reason: StorageFailure }>;

export type HistoryMergeResult =
  | Readonly<{ kind: "committed"; added: number; skipped: number }>
  | Readonly<{ kind: "conflict"; id: string }>
  | Readonly<{ kind: "unavailable"; reason: StorageFailure }>;

export type MatchHistoryPort = Readonly<{
  list(): Promise<HistoryLoadResult<readonly MatchHistorySummary[]>>;
  load(id: string): Promise<HistoryLoadResult<MatchRecordV1 | null>>;
  loadAll(): Promise<HistoryLoadResult<readonly MatchRecordV1[]>>;
  delete(id: string): Promise<HistoryMutationResult>;
  clear(): Promise<HistoryMutationResult>;
  merge(records: readonly MatchRecordV1[]): Promise<HistoryMergeResult>;
  subscribe(listener: () => void): () => void;
}>;

export function summarizeMatchRecord(record: MatchRecordV1): MatchHistorySummary {
  return Object.freeze(
    record.mode === "bot"
      ? {
          id: record.id,
          completedAt: record.completedAt,
          mode: record.mode,
          result: record.result,
          moveCount: record.moves.length,
          humanPlayer: record.humanPlayer,
          difficulty: record.difficulty,
        }
      : {
          id: record.id,
          completedAt: record.completedAt,
          mode: record.mode,
          result: record.result,
          moveCount: record.moves.length,
        },
  );
}
